import Box from '@mui/material/Box'
import { BarChart } from '@mui/x-charts/BarChart'
import { useId, useMemo } from 'react'

import { KestrixChartLegendFrame } from '../KestrixChartLegend/components/KestrixChartLegendFrame.tsx'
import { colorTokens } from '../tokens/colors.ts'
import { BarChartPatternDefs } from './patterns.tsx'
import { DEFAULT_COLORS } from './styles.ts'
import type { KestrixBarChartProps } from './types.ts'
import { processSeries } from './utils.ts'

export { CHART_CATEGORY_COLORS } from './styles.ts'

/**
 * Reusable Kestrix bar chart built on MUI x-charts.
 *
 * Supports single series, grouped (multiple series/categories),
 * stacked, and pattern-stacked variants via the `series` configuration:
 *
 * - **Single**: one entry in `series`
 * - **Grouped**: multiple entries without `stack`
 * - **Stacked**: multiple entries sharing the same `stack` value
 * - **Pattern-stacked**: stacked entries with a `pattern` property
 *
 * @example
 * ```tsx
 * <KestrixBarChart
 *   series={[
 *     { data: [10, 20, 30], label: 'Revenue', pattern: 'diagonal-right' },
 *     { data: [5, 15, 25], label: 'Costs', pattern: 'dotted', stack: 'total' },
 *   ]}
 *   categories={['Jan', 'Feb', 'Mar']}
 *   hideLegend
 * />
 * ```
 */
const DEFAULT_MARGIN = { top: 20, right: 20, bottom: 32, left: 20 }

const AXIS_STROKE = colorTokens.global.neutral[200]

const KestrixBarChart = ({
  series,
  categories,
  dataset,
  categoryColors,
  width,
  height,
  margin,
  layout = 'vertical',
  grid,
  hideCategoryAxisTooltip = false,
  tooltip = true,
  hideLegend = false,
  legendMaxVisible,
  borderRadius = 4,
  skipAnimation,
  onItemClick,
  slots,
  slotProps: chartSlotProps,
  sx,
  children
}: KestrixBarChartProps) => {
  const rawId = useId()
  const chartId = rawId.replace(/:/g, '')

  const { muiSeries, patternConfigs } = useMemo(
    () => processSeries(series, chartId),
    [series, chartId]
  )

  const mergedSlotProps = useMemo(() => {
    const userTooltip =
      chartSlotProps?.tooltip &&
      typeof chartSlotProps.tooltip === 'object' &&
      chartSlotProps.tooltip !== null
        ? chartSlotProps.tooltip
        : {}
    return {
      ...chartSlotProps,
      tooltip: {
        ...userTooltip,
        ...(!tooltip ? { trigger: 'none' as const } : {})
      }
    }
  }, [chartSlotProps, tooltip])

  const { xAxisConfig, yAxisConfig } = useMemo(() => {
    const ordinalColorMap = categoryColors
      ? {
          type: 'ordinal' as const,
          values: [...categories],
          colors: categoryColors
        }
      : undefined

    const bandAxisExtras = {
      tickLabelInterval: () => true
    } as const

    const xAxisConfig =
      layout === 'vertical'
        ? [
            {
              data: [...categories],
              scaleType: 'band' as const,
              colorMap: ordinalColorMap,
              height: 36,
              ...bandAxisExtras,
              ...(hideCategoryAxisTooltip ? { hideTooltip: true } : {})
            }
          ]
        : undefined

    const yAxisConfig =
      layout === 'horizontal'
        ? [
            {
              data: [...categories],
              scaleType: 'band' as const,
              colorMap: ordinalColorMap,
              width: 36,
              ...bandAxisExtras,
              ...(hideCategoryAxisTooltip ? { hideTooltip: true } : {})
            }
          ]
        : undefined

    return { xAxisConfig, yAxisConfig }
  }, [categories, categoryColors, hideCategoryAxisTooltip, layout])

  const legendItems = useMemo(
    () =>
      hideLegend
        ? []
        : series.flatMap((s, index) => {
            if (typeof s.label !== 'string' || s.label.length === 0) {
              return []
            }
            return [
              {
                id: s.label,
                label: s.label,
                color: s.color ?? DEFAULT_COLORS[index % DEFAULT_COLORS.length]!
              }
            ]
          }),
    [hideLegend, series]
  )

  const chart = (
    <BarChart
      series={muiSeries}
      {...(dataset !== undefined ? { dataset } : {})}
      xAxis={xAxisConfig}
      yAxis={yAxisConfig}
      layout={layout}
      {...(width !== undefined ? { width } : {})}
      {...(height !== undefined ? { height } : {})}
      margin={{ ...DEFAULT_MARGIN, ...margin }}
      grid={grid}
      hideLegend
      borderRadius={borderRadius}
      skipAnimation={skipAnimation}
      onItemClick={onItemClick}
      {...(slots !== undefined ? { slots } : {})}
      slotProps={mergedSlotProps}
      sx={{
        // Selector is intentionally specific to outrank the styles emitted by
        // x-charts' internal AxisRoot (same 2-class specificity as our sx, but
        // registered later in source order, so it would otherwise win).
        '& .MuiChartsAxis-root .MuiChartsAxis-line': { stroke: AXIS_STROKE },
        '& .MuiChartsAxis-root .MuiChartsAxis-tick': { stroke: AXIS_STROKE }
      }}
    >
      {patternConfigs.length > 0 && (
        <BarChartPatternDefs patterns={patternConfigs} />
      )}
      {children}
    </BarChart>
  )

  const frame = (
    <KestrixChartLegendFrame
      chart={chart}
      items={legendItems}
      maxVisible={legendMaxVisible}
      dataTestId="kestrix-bar-chart-legend"
    />
  )

  return sx === undefined ? frame : <Box sx={sx}>{frame}</Box>
}

export { KestrixBarChart }
